import type {
  LicenseFulfillmentDto,
  SubjectRequirementFulfillmentDto,
} from '../../services/licenseComplianceService';

/**
 * "Goal reached" detection for the Reporting page, in two tiers:
 * - License goal: the license becomes compliant — the big moment (confetti + message).
 * - Sub-goal: one requirement (ethics, a subject requirement, or a manual
 *   attestation) becomes met while the license is still in progress — a small
 *   moment (tile flash + chip pop).
 *
 * Each achievement celebrates exactly once: on the first visit where it is
 * observed, whether it was earned on this page or elsewhere. Celebrated keys
 * persist in localStorage per (license, reporting period), so a new reporting
 * period starts fresh. When the license goal fires (or already has), its
 * sub-goals are absorbed silently so the two tiers never stack.
 */

const STORAGE_KEY = 'cpax.compliance.celebrated';

/** Cap on persisted keys: keys are per (license, period), so old periods'
 *  keys go dead once a new period starts — drop oldest-first past the cap. */
const MAX_STORED_KEYS = 200;

/** Sub-goal id for the ethics requirement, which has no DTO id of its own. */
export const ETHICS_SUB_ID = 'ethics';

export function licenseGoalKey(license: LicenseFulfillmentDto): string {
  return `lic|${license.licenseId}|${license.periodEnd}`;
}

export function subGoalKey(license: LicenseFulfillmentDto, subId: string): string {
  return `sub|${license.licenseId}|${license.periodEnd}|${subId}`;
}

/** Met-state for one subject requirement, matching the panel's tile display logic. */
export function isSubjectRequirementMet(req: SubjectRequirementFulfillmentDto): boolean {
  if (req.targetKind === 'manual') return req.isMet;
  // required <= 0 means no such requirement (or missing data) — nothing to achieve
  if (req.requiredHours <= 0) return false;
  // An empty bucket array must not count as met ([].every() is vacuously true);
  // fall back to the backend's own aggregate judgment instead.
  return req.scope === 'per_year' && req.yearBuckets && req.yearBuckets.length > 0
    ? req.yearBuckets.every((b) => b.isMet)
    : req.isMet;
}

/** Ids of all currently-met sub-goals on a license (ethics uses ETHICS_SUB_ID). */
export function getMetSubGoalIds(license: LicenseFulfillmentDto): string[] {
  const ids: string[] = [];
  if (license.requiredEthicsHours > 0 && license.earnedEthicsHours >= license.requiredEthicsHours) {
    ids.push(ETHICS_SUB_ID);
  }
  for (const req of license.subjectRequirements) {
    if (isSubjectRequirementMet(req)) ids.push(req.id);
  }
  return ids;
}

export interface CelebrationPlan {
  /** Licenses that should fire the big goal-reached celebration now. */
  licenseIds: string[];
  /** licenseId -> sub-goal ids that should fire the small celebration now. */
  subGoalIds: Map<string, string[]>;
  /** Every newly observed achievement key to persist (fired or silently absorbed). */
  keysToMark: string[];
}

/**
 * Diffs current fulfillment against the set of already-celebrated keys.
 * Pure — storage is handled by readCelebratedKeys/markCelebratedKeys.
 */
export function planCelebrations(
  licenses: LicenseFulfillmentDto[],
  celebrated: ReadonlySet<string>
): CelebrationPlan {
  const plan: CelebrationPlan = { licenseIds: [], subGoalIds: new Map(), keysToMark: [] };
  for (const license of licenses) {
    const newSubIds = getMetSubGoalIds(license).filter(
      (id) => !celebrated.has(subGoalKey(license, id))
    );
    if (license.isCompliant) {
      const key = licenseGoalKey(license);
      // requiredTotalHours <= 0 means missing/unconfirmed data — a vacuous
      // "compliant" there is not an achievement, so never fire for it.
      if (license.requiredTotalHours > 0 && !celebrated.has(key)) {
        plan.licenseIds.push(license.licenseId);
        plan.keysToMark.push(key);
      }
      // The license goal (fired now or previously) covers its sub-goals: mark
      // them celebrated without firing so they can't flash on a later visit.
      for (const id of newSubIds) plan.keysToMark.push(subGoalKey(license, id));
    } else if (newSubIds.length > 0) {
      plan.subGoalIds.set(license.licenseId, newSubIds);
      for (const id of newSubIds) plan.keysToMark.push(subGoalKey(license, id));
    }
  }
  return plan;
}

/** Reads the persisted celebrated-key set. Storage failures read as empty. */
export function readCelebratedKeys(): Set<string> {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === 'string') : []);
  } catch {
    return new Set();
  }
}

/** Appends keys to the persisted set. Storage failures are ignored (celebration may repeat). */
export function markCelebratedKeys(keys: string[]): void {
  if (keys.length === 0) return;
  try {
    const merged = [...new Set([...readCelebratedKeys(), ...keys])];
    // Insertion order puts the newest keys last; keep those when trimming.
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(merged.slice(-MAX_STORED_KEYS)));
  } catch {
    // Non-critical: worst case the celebration fires again next visit.
  }
}
